import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { FieldValue } from "./form-data";

/**
 * Binds the forms Puck field components to a value store. Puck `<Render>` draws
 * the structure; this carries the input state. The live widget supplies a
 * controlled store and reads it back on submit; with no provider (the editor
 * preview) `useFormValue` falls back to local state so fields stay interactive.
 *
 * The optional `uploadImage` is how a photo field uploads a file and gets back a
 * stored URL — supplied only on a real booking surface (the editor preview has
 * none, so it shows a static placeholder instead).
 */
type UploadImage = (file: File) => Promise<string | null>;

interface FormValuesApi {
  getValue: (id: string) => FieldValue;
  setValue: (id: string, value: FieldValue) => void;
  uploadImage?: UploadImage;
  googleMapsApiKey?: string;
}

const FormValuesCtx = createContext<FormValuesApi | null>(null);

export function FormValuesProvider({
  values,
  onChange,
  uploadImage,
  googleMapsApiKey,
  children,
}: {
  values: Record<string, FieldValue>;
  onChange: (id: string, value: FieldValue) => void;
  uploadImage?: UploadImage;
  googleMapsApiKey?: string;
  children: ReactNode;
}) {
  const api = useMemo<FormValuesApi>(
    () => ({ getValue: (id) => values[id], setValue: onChange, uploadImage, googleMapsApiKey }),
    [values, onChange, uploadImage, googleMapsApiKey],
  );
  return <FormValuesCtx.Provider value={api}>{children}</FormValuesCtx.Provider>;
}

export function useFormValue(id: string): [FieldValue, (value: FieldValue) => void] {
  const api = useContext(FormValuesCtx);
  const [local, setLocal] = useState<FieldValue>(undefined);
  if (!api) return [local, setLocal];
  return [api.getValue(id), (value) => api.setValue(id, value)];
}

/** The surface's image uploader, or null in contexts that can't upload (the
 *  editor preview) — the photo field renders a placeholder when it's null. */
export function useFormUpload(): UploadImage | null {
  return useContext(FormValuesCtx)?.uploadImage ?? null;
}

/** The Google Maps key for the address field's autocomplete, or null in contexts
 *  that don't supply one (the editor preview) — the field falls back to a plain
 *  text input when it's null. */
export function useFormMapsKey(): string | null {
  return useContext(FormValuesCtx)?.googleMapsApiKey ?? null;
}
